import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Breadcrumbs,
  Link,
  Paper,
  Grid,
  CircularProgress,
  Alert,
  Button,
  Switch,
  FormControlLabel,
  FormGroup,
  Divider,
  Card,
  CardContent,
  CardHeader,
  Snackbar,
} from '@mui/material';
import {
  Home as HomeIcon,
  ArrowBack as ArrowBackIcon,
  Settings as SettingsIcon,
  Notifications as NotificationsIcon,
  Email as EmailIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useAuthenticatedFetch } from '../../../../hooks/useAuthenticatedFetch';
import { API_URL } from '../../../../utils/config';

interface NotificationSettings {
  emailNewPending: boolean;
  emailHighPriority: boolean;
  emailApprovalRequired: boolean;
  emailExecutionComplete: boolean;
  emailExecutionFailed: boolean;
  emailDailyDigest: boolean;
  inAppNewPending: boolean;
  inAppHighPriority: boolean;
  inAppApprovalRequired: boolean;
  inAppExecutionComplete: boolean;
  inAppExecutionFailed: boolean;
}

const defaultSettings: NotificationSettings = {
  emailNewPending: true,
  emailHighPriority: true,
  emailApprovalRequired: true,
  emailExecutionComplete: false,
  emailExecutionFailed: true,
  emailDailyDigest: true,
  inAppNewPending: true,
  inAppHighPriority: true,
  inAppApprovalRequired: true,
  inAppExecutionComplete: true,
  inAppExecutionFailed: true,
};

export const ReconciliationSettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { authenticatedFetch } = useAuthenticatedFetch();

  const [settings, setSettings] = useState<NotificationSettings>(defaultSettings);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await authenticatedFetch(`${API_URL}/api/accounting/reconciliation/settings`);
      if (!response) {
        throw new Error('No response received');
      }
      const data = await response.json();

      if (data.success && data.data) {
        setSettings({
          emailNewPending: data.data.emailNewPending ?? defaultSettings.emailNewPending,
          emailHighPriority: data.data.emailHighPriority ?? defaultSettings.emailHighPriority,
          emailApprovalRequired: data.data.emailApprovalRequired ?? defaultSettings.emailApprovalRequired,
          emailExecutionComplete: data.data.emailExecutionComplete ?? defaultSettings.emailExecutionComplete,
          emailExecutionFailed: data.data.emailExecutionFailed ?? defaultSettings.emailExecutionFailed,
          emailDailyDigest: data.data.emailDailyDigest ?? defaultSettings.emailDailyDigest,
          inAppNewPending: data.data.inAppNewPending ?? defaultSettings.inAppNewPending,
          inAppHighPriority: data.data.inAppHighPriority ?? defaultSettings.inAppHighPriority,
          inAppApprovalRequired: data.data.inAppApprovalRequired ?? defaultSettings.inAppApprovalRequired,
          inAppExecutionComplete: data.data.inAppExecutionComplete ?? defaultSettings.inAppExecutionComplete,
          inAppExecutionFailed: data.data.inAppExecutionFailed ?? defaultSettings.inAppExecutionFailed,
        });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, [authenticatedFetch]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleChange = (field: keyof NotificationSettings) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setSettings(prev => ({ ...prev, [field]: event.target.checked }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      const response = await authenticatedFetch(`${API_URL}/api/accounting/reconciliation/settings/update`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settings),
      });

      if (!response) {
        throw new Error('No response received');
      }
      const data = await response.json();

      if (data.success) {
        setSuccess(true);
        setHasChanges(false);
      } else {
        setError(data.error || 'Failed to save settings');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          underline="hover"
          color="inherit"
          href="#"
          onClick={() => navigate('/')}
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          Home
        </Link>
        <Link
          underline="hover"
          color="inherit"
          href="#"
          onClick={() => navigate('/admin/hub')}
        >
          Admin Hub
        </Link>
        <Link
          underline="hover"
          color="inherit"
          href="#"
          onClick={() => navigate('/admin/hub/accounting/reconciliation')}
        >
          Reconciliation
        </Link>
        <Typography color="text.primary">Settings</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/admin/hub/accounting/reconciliation')}
            sx={{ mr: 2 }}
          >
            Back
          </Button>
          <SettingsIcon sx={{ mr: 1, fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" component="h1">
            Reconciliation Settings
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving || !hasChanges}
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Email Notifications */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader
              avatar={<EmailIcon color="primary" />}
              title="Email Notifications"
              subheader="Configure which events trigger email notifications"
            />
            <CardContent>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.emailNewPending}
                      onChange={handleChange('emailNewPending')}
                    />
                  }
                  label="New pending reconciliation items"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.emailHighPriority}
                      onChange={handleChange('emailHighPriority')}
                    />
                  }
                  label="High priority items requiring attention"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.emailApprovalRequired}
                      onChange={handleChange('emailApprovalRequired')}
                    />
                  }
                  label="Items requiring your approval"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.emailExecutionComplete}
                      onChange={handleChange('emailExecutionComplete')}
                    />
                  }
                  label="Payment execution completed"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.emailExecutionFailed}
                      onChange={handleChange('emailExecutionFailed')}
                    />
                  }
                  label="Payment execution failed"
                />
                <Divider sx={{ my: 2 }} />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.emailDailyDigest}
                      onChange={handleChange('emailDailyDigest')}
                    />
                  }
                  label="Daily digest summary (8:00 AM)"
                />
              </FormGroup>
            </CardContent>
          </Card>
        </Grid>

        {/* In-App Notifications */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader
              avatar={<NotificationsIcon color="primary" />}
              title="In-App Notifications"
              subheader="Configure which events show in-app notifications"
            />
            <CardContent>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.inAppNewPending}
                      onChange={handleChange('inAppNewPending')}
                    />
                  }
                  label="New pending reconciliation items"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.inAppHighPriority}
                      onChange={handleChange('inAppHighPriority')}
                    />
                  }
                  label="High priority items requiring attention"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.inAppApprovalRequired}
                      onChange={handleChange('inAppApprovalRequired')}
                    />
                  }
                  label="Items requiring your approval"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.inAppExecutionComplete}
                      onChange={handleChange('inAppExecutionComplete')}
                    />
                  }
                  label="Payment execution completed"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.inAppExecutionFailed}
                      onChange={handleChange('inAppExecutionFailed')}
                    />
                  }
                  label="Payment execution failed"
                />
              </FormGroup>
            </CardContent>
          </Card>
        </Grid>

        {/* Information Card */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3, bgcolor: 'grey.100' }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              About Notifications
            </Typography>
            <Typography variant="body2" paragraph>
              Email notifications are sent to your registered email address. The daily digest
              provides a summary of all pending reconciliation items and is sent at 8:00 AM
              Central Time.
            </Typography>
            <Typography variant="body2">
              In-app notifications appear in the notification bell icon in the top navigation bar.
              They are visible when you are logged into the platform.
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Success Snackbar */}
      <Snackbar
        open={success}
        autoHideDuration={4000}
        onClose={() => setSuccess(false)}
        message="Settings saved successfully"
      />
    </Container>
  );
};

export default ReconciliationSettingsPage;
